import { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, Plus, Edit2, Trash2, Check, FileCheck, AlertCircle } from '../../icons';
// 채택 3단계 ② 인사 — 단추를 정본 Button 으로(D1).
import { Button } from '../../components/shared';
import {
  listRecords, getRecords, createEducation, updateEducation, deleteEducation,
  createCareer, updateCareer, deleteCareer, periodText,
  EDUCATION_LEVELS, EDUCATION_LEVEL_LABEL, EDUCATION_STATES, EDUCATION_STATE_LABEL,
  type RecordRowDto, type RecordListDto, type RecordDetailDto, type RecordFilter,
  type EducationDto, type CareerDto,
} from '../../api/hr-records';
// 손짠 머리 → 정본 PageHeader · 경로 · 범위 칩(2026-10-17 · web 레인 W7a — lib/page-crumbs.ts)
import { PageHeader } from '../../components/shared';
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';
import './RecordsPage.css';

/**
 * 경력 · 학력 — 담당자 화면 (시안 A19, migration 132).
 *
 * ★★ **왼쪽은 사람, 오른쪽은 그 사람의 이력**이다. 시안이 그렇게 그린 것은 이 일이
 *    «한 사람을 열어 채우는 일» 이기 때문이다 — 목록만 있으면 누가 비었는지는 알아도
 *    채울 수가 없고, 상세만 있으면 누구를 채워야 하는지 모른다.
 * ★ **확인과 증빙은 따로 누른다.** 확인은 담당자가 «맞다» 고 하는 것이고, 증빙은 서류가
 *   붙었다는 것이다 — 하나로 합치면 «서류 없이 확인» 을 말할 수 없다.
 * ★ 못 읽으면 **화면을 접는다**(옛 서버·권한 어느 쪽이든) — 인사 홈·팀 근태와 같은 태도.
 */

type FormKind = 'edu' | 'career';

interface EduForm {
  id?: string; level: string; schoolName: string; major: string;
  startYm: string; endYm: string; state: string; hasProof: boolean;
}
interface CareerForm {
  id?: string; companyName: string; position: string; duty: string;
  startYm: string; endYm: string; hasProof: boolean;
}

const emptyEdu: EduForm = {
  level: 'BACHELOR', schoolName: '', major: '', startYm: '', endYm: '', state: 'GRADUATED', hasProof: false,
};
const emptyCareer: CareerForm = {
  companyName: '', position: '', duty: '', startYm: '', endYm: '', hasProof: false,
};

const PROOF_LABEL: Record<string, string> = { ALL: '제출', SOME: '일부', NONE: '미제출' };
const PROOF_TONE: Record<string, string> = { ALL: 'is-ok', SOME: 'is-wait', NONE: 'is-no' };

const FILTERS: Array<{ key: RecordFilter; label: string }> = [
  { key: 'all', label: '전체' },
  { key: 'eduMissing', label: '학력 미등록' },
  { key: 'proofMissing', label: '증빙 미제출' },
];

/** `YYYY-MM` 칸 — 브라우저 `month` 입력을 쓴다(달까지만 적는 값이라 날짜 칸은 거짓말이 된다). */
function MonthField({ label, value, onChange, required }: {
  label: string; value: string; onChange: (v: string) => void; required?: boolean;
}) {
  return (
    <label className="hrcert__field">
      <span>{label}{required ? ' *' : ''}</span>
      <input type="month" value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

export function RecordsPage() {
  const [list, setList] = useState<RecordListDto | null>(null);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<RecordFilter>('all');
  const [state, setState] = useState<'loading' | 'ok' | 'folded'>('loading');
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<RecordDetailDto | null>(null);
  const [form, setForm] = useState<FormKind | null>(null);
  const [edu, setEdu] = useState<EduForm>(emptyEdu);
  const [career, setCareer] = useState<CareerForm>(emptyCareer);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadList = useCallback(async () => {
    const res = await listRecords({
      ...(query ? { search: query } : {}),
      ...(filter !== 'all' ? { filter } : {}),
    });
    setList(res);
    return res;
  }, [query, filter]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await loadList();
        if (!alive) return;
        setState('ok');
        // 처음 들어오면 맨 위 사람을 연다 — 오른쪽 칸이 비어 있으면 무엇을 하는 화면인지 안 보인다.
        setPickedId((cur) => cur ?? res.rows[0]?.userId ?? null);
      } catch {
        if (alive) setState('folded');
      }
    })();
    return () => { alive = false; };
  }, [loadList]);

  const loadDetail = useCallback(async (userId: string) => {
    try {
      setDetail(await getRecords(userId));
    } catch {
      setDetail(null);
    }
  }, []);

  useEffect(() => {
    if (!pickedId) { setDetail(null); return; }
    void loadDetail(pickedId);
  }, [pickedId, loadDetail]);

  const picked = useMemo(
    () => list?.rows.find((r) => r.userId === pickedId) ?? null,
    [list, pickedId],
  );

  const act = async (run: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await run();
      if (pickedId) await loadDetail(pickedId);
      await loadList();
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? '처리하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const submitEdu = async () => {
    if (!pickedId) return;
    if (!edu.schoolName.trim()) { setError('학교 이름을 적어 주세요.'); return; }
    const body = {
      level: edu.level,
      schoolName: edu.schoolName.trim(),
      major: edu.major.trim() || null,
      startYm: edu.startYm || null,
      endYm: edu.endYm || null,
      state: edu.state,
      hasProof: edu.hasProof,
    };
    await act(async () => {
      if (edu.id) await updateEducation(edu.id, body);
      else await createEducation(pickedId, body);
      setForm(null);
      setEdu(emptyEdu);
    });
  };

  const submitCareer = async () => {
    if (!pickedId) return;
    if (!career.companyName.trim()) { setError('회사 이름을 적어 주세요.'); return; }
    if (!career.startYm || !career.endYm) { setError('경력은 시작과 종료를 모두 적어야 합니다.'); return; }
    const body = {
      companyName: career.companyName.trim(),
      position: career.position.trim() || null,
      duty: career.duty.trim() || null,
      startYm: career.startYm,
      endYm: career.endYm,
      hasProof: career.hasProof,
    };
    await act(async () => {
      if (career.id) await updateCareer(career.id, body);
      else await createCareer(pickedId, body);
      setForm(null);
      setCareer(emptyCareer);
    });
  };

  const editEdu = (e: EducationDto) => {
    setEdu({
      id: e.id, level: e.level, schoolName: e.schoolName, major: e.major ?? '',
      startYm: e.startYm ?? '', endYm: e.endYm ?? '', state: e.state, hasProof: e.hasProof,
    });
    setForm('edu');
  };
  const editCareer = (c: CareerDto) => {
    setCareer({
      id: c.id, companyName: c.companyName, position: c.position ?? '', duty: c.duty ?? '',
      startYm: c.startYm, endYm: c.endYm, hasProof: c.hasProof,
    });
    setForm('career');
  };

  const removeEdu = (e: EducationDto) => {
    if (!window.confirm(`${e.schoolName} 학력을 지웁니다. 되돌릴 수 없습니다.`)) return;
    void act(() => deleteEducation(e.id));
  };
  const removeCareer = (c: CareerDto) => {
    if (!window.confirm(`${c.companyName} 경력을 지웁니다. 되돌릴 수 없습니다.`)) return;
    void act(() => deleteCareer(c.id));
  };

  return (
    <div className="hrpage">
      <PageHeader
        breadcrumb={crumbsFor('/erp/hr/records/career')}
        scope={scopeFor('/erp/hr/records/career')}
        title="경력 · 학력"
        description="왼쪽에서 사람을 고르고 오른쪽에서 채웁니다 — 경력은 호봉 · 직급 산정과 경력증명서에 들어갑니다"
      />

      {state === 'loading' && <div className="hrpage__note">불러오는 중…</div>}
      {state === 'folded' && (
        <div className="hrpage__note">
          인사기록을 불러오지 못했습니다 — 회사 관리자만 볼 수 있고, 서버에 이 화면의 표가 있어야 합니다.
        </div>
      )}

      {state === 'ok' && list && (
        <>
          {/*
            KPI 카드 넷은 걷었다(2026-10-18 · web 레인 W10-55 · 렌즈 Toss · 사용자 승인 «요약 카드 줄 걷기»).
            할 일 수(학력 미등록 · 증빙 미제출)는 아래 거르기 칩에 남는다. 경력 등록 · 평균 총경력은 이 화면에서
            빠진다 — 시안 55 md: 필요하면 인사 통계(26) 근속 칸 옆(서버 값은 그대로).
            ★ 칩 수는 `summary` 다 — 서버가 거르기 전 전체 줄로 센다(records.routes.ts · filter 는 rows 에만).
          */}

          {error && <p className="hrcert__err">{error}</p>}

          <div className="hrrec__split">
            <section className="hrpage__panel">
              <h2 className="hrpage__panelt">
                직원별 이력
                <span className="hrpage__panels">{list.rows.length}명</span>
              </h2>

              <form
                className="hrrec__bar"
                onSubmit={(e) => { e.preventDefault(); setQuery(search.trim()); }}
              >
                <div className="hrrec__search">
                  <Search size={15} aria-hidden />
                  <input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="이름 · 사번으로 찾기"
                    aria-label="이름 · 사번으로 찾기"
                  />
                </div>
                <div className="hrcert__tabs" role="group" aria-label="거르기">
                  {FILTERS.map((f) => (
                    <button
                      key={f.key}
                      type="button"
                      className={`hrcert__tab${filter === f.key ? ' is-on' : ''}`}
                      onClick={() => setFilter(f.key)}
                      aria-pressed={filter === f.key}
                    >
                      {f.label}
                      {f.key === 'eduMissing' && list.summary.eduMissing > 0 && (
                        <span className="hrcert__count">{list.summary.eduMissing}</span>
                      )}
                      {/* 증빙은 «항목» 을 센다(사람이 아니다 · 결정 D11) — 칩이 거르는 것은 사람이라 단위를 글로 적는다. */}
                      {f.key === 'proofMissing' && list.summary.proofMissingItems > 0 && (
                        <span className="hrcert__count">항목 {list.summary.proofMissingItems}</span>
                      )}
                    </button>
                  ))}
                </div>
              </form>

              {list.rows.length === 0 ? (
                <p className="hrpage__empty">해당하는 사람이 없습니다.</p>
              ) : (
                <table className="hrpage__tbl">
                  <thead>
                    <tr>
                      <th>직원</th>
                      <th>최종학력</th>
                      <th>전공</th>
                      <th className="hrpage__num">입사 전 경력</th>
                      <th className="hrpage__num">경력 건</th>
                      <th className="hrpage__num">증빙</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.rows.map((r: RecordRowDto) => (
                      <tr
                        key={r.userId}
                        className={r.userId === pickedId ? 'is-picked' : ''}
                        onClick={() => { setPickedId(r.userId); setForm(null); }}
                      >
                        <td>
                          <button type="button" className="hrrec__who">
                            <b>{r.name}</b>
                            <span className="hrpage__dim">{r.teamName ?? '회사 직속'}</span>
                          </button>
                        </td>
                        <td>
                          {r.finalLevelLabel ?? <span className="hrpage__pill">미등록</span>}
                        </td>
                        <td className="hrpage__dim">{r.major ?? '—'}</td>
                        <td className="hrpage__num">{r.careerYears}년</td>
                        <td className="hrpage__num">{r.careerCount}</td>
                        <td className="hrpage__num">
                          <span className={`hrpage__pill ${PROOF_TONE[r.proof]}`}>{PROOF_LABEL[r.proof]}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            <div className="hrrec__side">
              {!detail ? (
                <section className="hrpage__panel">
                  <h2 className="hrpage__panelt">이력</h2>
                  <p className="hrpage__empty">왼쪽에서 사람을 고르면 그 사람의 학력·경력이 열립니다.</p>
                </section>
              ) : (
                <>
                  <section className="hrpage__panel">
                    <h2 className="hrpage__panelt">
                      {detail.user.name} · 학력
                      <span className="hrpage__panels">{detail.user.teamName ?? '회사 직속'}</span>
                      <button
                        type="button"
                        className="hrrec__add"
                        onClick={() => { setEdu(emptyEdu); setForm(form === 'edu' ? null : 'edu'); }}
                      >
                        <Plus size={14} aria-hidden />학력
                      </button>
                    </h2>

                    {form === 'edu' && (
                      <div className="hrcert__form">
                        <label className="hrcert__field">
                          <span>구분 *</span>
                          <select value={edu.level} onChange={(e) => setEdu({ ...edu, level: e.target.value })}>
                            {EDUCATION_LEVELS.map((l) => (
                              <option key={l} value={l}>{EDUCATION_LEVEL_LABEL[l]}</option>
                            ))}
                          </select>
                        </label>
                        <label className="hrcert__field">
                          <span>학교 *</span>
                          <input value={edu.schoolName} onChange={(e) => setEdu({ ...edu, schoolName: e.target.value })} />
                        </label>
                        <label className="hrcert__field">
                          <span>전공</span>
                          <input value={edu.major} onChange={(e) => setEdu({ ...edu, major: e.target.value })} />
                        </label>
                        <MonthField label="입학" value={edu.startYm} onChange={(v) => setEdu({ ...edu, startYm: v })} />
                        <MonthField label="졸업" value={edu.endYm} onChange={(v) => setEdu({ ...edu, endYm: v })} />
                        <label className="hrcert__field">
                          <span>상태</span>
                          <select value={edu.state} onChange={(e) => setEdu({ ...edu, state: e.target.value })}>
                            {EDUCATION_STATES.map((s) => (
                              <option key={s} value={s}>{EDUCATION_STATE_LABEL[s]}</option>
                            ))}
                          </select>
                        </label>
                        <label className="hroff__check">
                          <input
                            type="checkbox"
                            checked={edu.hasProof}
                            onChange={(e) => setEdu({ ...edu, hasProof: e.target.checked })}
                          />
                          <span>증빙 제출됨</span>
                        </label>
                        <Button variant="primary" onClick={submitEdu} disabled={busy}>
                          {edu.id ? '저장' : '등록'}
                        </Button>
                      </div>
                    )}

                    {detail.educations.length === 0 ? (
                      <p className="hrpage__empty">등록된 학력이 없습니다.</p>
                    ) : (
                      <table className="hrpage__tbl">
                        <thead>
                          <tr>
                            <th>구분</th>
                            <th>학교 · 전공</th>
                            <th>기간</th>
                            <th className="hrpage__act">증빙 · 확인</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.educations.map((e) => (
                            <tr key={e.id}>
                              <td className="hrrec__nowrap">
                                {e.levelLabel}
                                {e.state !== 'GRADUATED' && <span className="hrpage__dim"> {e.stateLabel}</span>}
                              </td>
                              <td>
                                {e.schoolName}
                                {e.major && <span className="hrpage__dim"> {e.major}</span>}
                                {e.selfEntered && !e.confirmedAt && (
                                  <span className="hrasg__flag">본인 입력</span>
                                )}
                              </td>
                              <td className="hrpage__dim hrrec__nowrap">{periodText(e.startYm, e.endYm)}</td>
                              <td className="hrpage__act hrrec__nowrap">
                                <button
                                  type="button"
                                  className={`hrrec__chip${e.hasProof ? ' is-on' : ''}`}
                                  onClick={() => act(() => updateEducation(e.id, { hasProof: !e.hasProof }))}
                                  disabled={busy}
                                  title="증빙 서류"
                                >
                                  <FileCheck size={13} aria-hidden />증빙
                                </button>
                                <button
                                  type="button"
                                  className={`hrrec__chip${e.confirmedAt ? ' is-on' : ''}`}
                                  onClick={() => act(() => updateEducation(e.id, { confirmed: !e.confirmedAt }))}
                                  disabled={busy}
                                  title="담당자 확인"
                                >
                                  <Check size={13} aria-hidden />확인
                                </button>
                                <Button variant="ghost" size="sm" iconOnly onClick={() => editEdu(e)} aria-label="수정">
                                  <Edit2 size={14} aria-hidden />
                                </Button>
                                <Button variant="ghost" size="sm" iconOnly onClick={() => removeEdu(e)} aria-label="삭제">
                                  <Trash2 size={14} aria-hidden />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </section>

                  <section className="hrpage__panel">
                    <h2 className="hrpage__panelt">
                      {detail.user.name} · 경력
                      <span className="hrpage__panels">합계 {detail.summary.careerYears}년</span>
                      <button
                        type="button"
                        className="hrrec__add"
                        onClick={() => { setCareer(emptyCareer); setForm(form === 'career' ? null : 'career'); }}
                      >
                        <Plus size={14} aria-hidden />경력
                      </button>
                    </h2>

                    {form === 'career' && (
                      <div className="hrcert__form">
                        <label className="hrcert__field">
                          <span>회사 *</span>
                          <input value={career.companyName} onChange={(e) => setCareer({ ...career, companyName: e.target.value })} />
                        </label>
                        <label className="hrcert__field">
                          <span>직위</span>
                          <input value={career.position} onChange={(e) => setCareer({ ...career, position: e.target.value })} />
                        </label>
                        <label className="hrcert__field">
                          <span>담당</span>
                          <input value={career.duty} onChange={(e) => setCareer({ ...career, duty: e.target.value })} />
                        </label>
                        <MonthField label="입사" required value={career.startYm} onChange={(v) => setCareer({ ...career, startYm: v })} />
                        <MonthField label="퇴사" required value={career.endYm} onChange={(v) => setCareer({ ...career, endYm: v })} />
                        <label className="hroff__check">
                          <input
                            type="checkbox"
                            checked={career.hasProof}
                            onChange={(e) => setCareer({ ...career, hasProof: e.target.checked })}
                          />
                          <span>증빙 제출됨</span>
                        </label>
                        <Button variant="primary" onClick={submitCareer} disabled={busy}>
                          {career.id ? '저장' : '등록'}
                        </Button>
                      </div>
                    )}

                    {detail.careers.length === 0 ? (
                      <p className="hrpage__empty">등록된 경력이 없습니다.</p>
                    ) : (
                      <table className="hrpage__tbl">
                        <thead>
                          <tr>
                            <th>회사</th>
                            <th>직위 · 담당</th>
                            <th>기간</th>
                            <th className="hrpage__act">증빙 · 확인</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.careers.map((c) => (
                            <tr key={c.id}>
                              <td>
                                {c.companyName}
                                {c.selfEntered && !c.confirmedAt && <span className="hrasg__flag">본인 입력</span>}
                              </td>
                              <td className="hrpage__dim">
                                {[c.position, c.duty].filter(Boolean).join(' · ') || '—'}
                              </td>
                              <td className="hrpage__dim hrrec__nowrap">
                                {periodText(c.startYm, c.endYm)}
                                <span className="hrrec__years"> {c.years}년</span>
                              </td>
                              <td className="hrpage__act hrrec__nowrap">
                                <button
                                  type="button"
                                  className={`hrrec__chip${c.hasProof ? ' is-on' : ''}`}
                                  onClick={() => act(() => updateCareer(c.id, { hasProof: !c.hasProof }))}
                                  disabled={busy}
                                  title="증빙 서류"
                                >
                                  <FileCheck size={13} aria-hidden />증빙
                                </button>
                                <button
                                  type="button"
                                  className={`hrrec__chip${c.confirmedAt ? ' is-on' : ''}`}
                                  onClick={() => act(() => updateCareer(c.id, { confirmed: !c.confirmedAt }))}
                                  disabled={busy}
                                  title="담당자 확인"
                                >
                                  <Check size={13} aria-hidden />확인
                                </button>
                                <Button variant="ghost" size="sm" iconOnly onClick={() => editCareer(c)} aria-label="수정">
                                  <Edit2 size={14} aria-hidden />
                                </Button>
                                <Button variant="ghost" size="sm" iconOnly onClick={() => removeCareer(c)} aria-label="삭제">
                                  <Trash2 size={14} aria-hidden />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </section>

                  <section className="hrpage__panel">
                    <h2 className="hrpage__panelt">이 값이 어디에 쓰이나</h2>
                    <ul className="hrrec__notes">
                      <li>
                        <AlertCircle size={14} aria-hidden />
                        <span>
                          경력은 <b>호봉 · 직급 산정</b>과 <b>경력증명서</b>에 들어갑니다. 그래서 담당자가
                          확인하기 전까지 「본인 입력」 표시가 남고, 증빙이 붙어야 확정입니다.
                        </span>
                      </li>
                      <li>
                        <AlertCircle size={14} aria-hidden />
                        <span>
                          <b>확인과 증빙은 다른 축</b>입니다 — 서류 없이 확인만 한 항목은 여기서 그대로 보입니다.
                          {detail.summary.unconfirmed > 0 && ` 지금 미확인 ${detail.summary.unconfirmed}건.`}
                        </span>
                      </li>
                      <li>
                        <AlertCircle size={14} aria-hidden />
                        <span>
                          기간은 <b>달까지만</b> 적습니다. 경력은 시작과 종료가 모두 필요합니다 —
                          입사 전 경력이라 «재직 중» 일 수 없습니다.
                        </span>
                      </li>
                    </ul>
                  </section>
                </>
              )}
              {picked && !detail && (
                <p className="hrpage__empty">{picked.name} 의 이력을 불러오지 못했습니다.</p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
